import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('paf_token'));

  const getAuthHeaders = useCallback(() => {
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  }, [token]);

  const apiCall = useCallback(async (method, url, data = null, config = {}) => {
    const { headers: configHeaders, ...restConfig } = config;
    const headers = { ...getAuthHeaders(), ...configHeaders };
    return axios({ method, url: `${API}${url}`, data, ...restConfig, headers });
  }, [getAuthHeaders]);

  const checkAuth = useCallback(async () => {
    try {
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await axios.get(`${API}/auth/me`, { headers });
      setUser(res.data);
      return true;
    } catch {
      setUser(null);
      setToken(null);
      localStorage.removeItem('paf_token');
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const { token: jwt, user: userData } = res.data;
    setToken(jwt);
    localStorage.setItem('paf_token', jwt);
    setUser(userData);
    return userData;
  };

  const signup = async (email, password, firstName, lastName, role) => {
    const res = await axios.post(`${API}/auth/signup`, { email, password, first_name: firstName, last_name: lastName, role });
    const { token: jwt, user: userData } = res.data;
    setToken(jwt);
    localStorage.setItem('paf_token', jwt);
    setUser(userData);
    return { user: userData };
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { headers: getAuthHeaders() });
    } catch {}
    setUser(null);
    setToken(null);
    localStorage.removeItem('paf_token');
  };

  return (
    <AuthContext.Provider value={{ user, loading, token, login, signup, logout, apiCall, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
